import { content as contentV2 } from '@opallabs/api/v2'
import { moments } from '@opallabs/api/v3'
import { type CalendarItem } from '../types'

/**
 * The ids the product's own endpoints are keyed by.
 *
 * A moment is written through `/moments/v3`, which knows it by the uuid the
 * month is already keyed by. Content has no v3 write worth the name — no
 * writable name, no delete — so everything about it goes through `/content/v2`
 * under the legacy id. An item that carries no legacy id therefore cannot be
 * acted on at all, and the caller has to say so rather than guess.
 */
export class MissingCalendarItemId extends Error {
  constructor(item: CalendarItem) {
    super(`${item.kind} ${item.id} has no legacy id to act on.`)
    this.name = 'MissingCalendarItemId'
  }
}

const legacyIdOf = (item: CalendarItem): string => {
  if (item.legacyId === undefined) throw new MissingCalendarItemId(item)
  return item.legacyId
}

export const renameMoment = ({
  momentId,
  title,
}: {
  momentId: string
  title: string
}) =>
  moments.update({
    params: { path: { moment_id: momentId }, query: {} },
    requestBody: {
      data: { attributes: { title }, id: momentId, type: 'moment' },
    },
  })

export const renameContent = ({
  item,
  name,
}: {
  item: CalendarItem
  name: string
}) => {
  const contentId = legacyIdOf(item)

  return contentV2.update({
    params: { path: { content_id: contentId } },
    requestBody: {
      data: {
        attributes: { name },
        id: contentId,
        relationships: {},
        type: 'content',
      },
    },
  })
}

/**
 * Copies a piece of content, with everything on it.
 *
 * The copying is the server's: naming the source in `duplicated_from_moment`
 * and `duplicated_from_post` runs the same `PostDuplicator` the week view's
 * Duplicate runs, so assets, labels, schedule and options all come along.
 *
 * The source is read first because the create needs its channel, and the month
 * knows a channel only by uuid while this endpoint takes the v2 primary key.
 * The copy keeps the source's position, so it lands beside what it came from.
 */
export const duplicateContent = async ({ item }: { item: CalendarItem }) => {
  const contentId = legacyIdOf(item)

  const document = await contentV2.readSingle({
    path: { content_id: contentId },
    query: {},
  })

  const momentId = document.data.relationships.moment.data.id
  const serviceId = document.data.relationships.service.data.id

  return contentV2.create({
    requestBody: {
      data: {
        attributes: { position: document.data.attributes.position },
        relationships: {
          duplicated_from_moment: { data: { id: momentId, type: 'moment' } },
          duplicated_from_post: { data: { id: contentId, type: 'content' } },
          moment: { data: { id: momentId, type: 'moment' } },
          service: { data: { id: serviceId, type: 'service' } },
        },
        type: 'content',
      },
    },
  })
}

/** Deletes the moment and, on the server's own time, everything inside it. */
export const deleteMoment = ({ momentId }: { momentId: string }) =>
  moments.delete({ params: { path: { moment_id: momentId } } })

export const deleteContent = ({ item }: { item: CalendarItem }) =>
  contentV2.delete({ params: { path: { content_id: legacyIdOf(item) } } })
